import { useEffect, useState } from 'react';
import type { UpdateWebrtcSettingsInput } from '@klaro7/validators';
import { Badge, Button, Input, Spinner } from '@klaro7/ui';

import { usePinGate } from '@/components/PinGate';
import { useWebrtcSettings, useUpdateWebrtcSettings, useTestTurn } from '../hooks/useServicesSettings';

function extractErrorDetail(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = err as {
      response?: {
        data?: { error?: { message?: string }; message?: string };
        status?: number;
        statusText?: string;
      };
    };
    const apiMsg = resp.response?.data?.error?.message || resp.response?.data?.message;
    const status = resp.response?.status;
    if (apiMsg) return `[${status}] ${apiMsg}`;
    if (status) return `HTTP ${status}: ${resp.response?.statusText || 'Errore sconosciuto'}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * WebRTC / TURN configuration. Super-admin only — the parent gates rendering.
 *
 * turnCredential arrives masked from the server and is included in the PUT payload
 * only when the admin types a new value (mirrors Stripe/SMS), so a stored secret is
 * never overwritten with its mask. The TURN server is the self-hosted coturn that
 * relays media when peers can't connect directly (essential for mobile/NAT).
 */
export function WebrtcSettingsSection() {
  const { requirePin, pinDialog } = usePinGate();
  const { data: settings, isLoading } = useWebrtcSettings(true);
  const updateMutation = useUpdateWebrtcSettings();
  const testMutation = useTestTurn();

  const [turnUrl, setTurnUrl] = useState('');
  const [turnUsername, setTurnUsername] = useState('');
  const [turnCredential, setTurnCredential] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [savedCredential, setSavedCredential] = useState('');

  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (settings) {
      setTurnUrl(settings.turnUrl ?? '');
      setTurnUsername(settings.turnUsername ?? '');
      setTurnCredential(settings.turnCredential ?? '');
      setEnabled(settings.enabled ?? false);
      setSavedCredential(settings.turnCredential ?? '');
    }
  }, [settings]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.type === 'error' ? 15000 : 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  async function handleSave() {
    const payload: UpdateWebrtcSettingsInput = { turnUrl, turnUsername, enabled };
    if (turnCredential !== savedCredential) {
      payload.turnCredential = turnCredential;
    }
    try {
      await updateMutation.mutateAsync(payload);
      setToast({ type: 'success', message: 'Configurazione WebRTC salvata con successo' });
    } catch (err) {
      setToast({ type: 'error', message: `Errore nel salvataggio WebRTC: ${extractErrorDetail(err)}` });
    }
  }

  async function handleTest() {
    try {
      const r = await testMutation.mutateAsync();
      if (r.ok) {
        setToast({ type: 'success', message: `Relay OK (${r.relayAddress}) — TURN funzionante` });
      } else if (r.reason === 'auth') {
        setToast({ type: 'error', message: 'Autenticazione fallita: username/credential TURN non corretti' });
      } else if (r.reason === 'unreachable') {
        setToast({ type: 'error', message: 'Server TURN irraggiungibile (firewall o server offline)' });
      } else {
        setToast({ type: 'error', message: 'Configura prima URL, username e credenziale TURN' });
      }
    } catch (err) {
      setToast({ type: 'error', message: `Errore nel test TURN: ${extractErrorDetail(err)}` });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 12.75c4.97-4.97 13.03-4.97 18 0M5.652 16.15a8.25 8.25 0 0111.196 0M9.054 19.553a3.75 3.75 0 015.892 0M12 22.5h.008v.008H12V22.5z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">WebRTC (TURN)</h3>
            <p className="text-xs text-neutral-600">Server TURN per le chiamate su mobile/NAT (solo super admin)</p>
          </div>
        </div>
        <Badge variant={enabled ? 'success' : 'default'}>
          {enabled ? 'Attivo' : 'Disattivato'}
        </Badge>
      </div>

      <div className="space-y-4 p-5">
        {toast && (
          <div
            className={`rounded-lg border px-4 py-2.5 text-sm font-medium ${
              toast.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {toast.message}
          </div>
        )}

        <Input
          label="TURN URL"
          value={turnUrl}
          onChange={(e) => setTurnUrl(e.target.value)}
          placeholder="turn:turn.klaro7.com:3478"
          helperText="URL del server coturn self-hosted (es. turn:host:3478). Salvato in chiaro."
        />

        <Input
          label="TURN Username"
          value={turnUsername}
          onChange={(e) => setTurnUsername(e.target.value)}
          placeholder="klaro7"
          helperText="Username configurato sul server coturn. Salvato in chiaro."
        />

        <Input
          label="TURN Credential"
          type="password"
          value={turnCredential}
          onChange={(e) => setTurnCredential(e.target.value)}
          placeholder="••••••••••••••••"
          helperText="Password/credenziale TURN, cifrata. Lascia invariato il valore mascherato per non sovrascriverla."
        />

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
          />
          Abilitato
        </label>

        <p className="rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3 text-xs text-neutral-600">
          Il server TURN (coturn self-hosted) inoltra il traffico audio/video quando i
          dispositivi non riescono a connettersi direttamente — indispensabile per le
          chiamate da mobile e dietro NAT/firewall restrittivi. Se disabilitato, viene
          usata solo la configurazione TURN da variabili d&apos;ambiente (se presente).
        </p>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-100 pt-4">
          <Button variant="outline" onClick={handleTest} loading={testMutation.isPending}>
            Testa TURN
          </Button>
          {pinDialog}
          <Button onClick={() => requirePin(handleSave)} loading={updateMutation.isPending}>
            Salva WebRTC
          </Button>
        </div>
      </div>
    </div>
  );
}
